import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import DomainSelector from './DomainSelector';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import LaunchIcon from '@mui/icons-material/Launch';
import { supabase } from '../supabaseClient';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

// STEP 0: Domain → Agent mapping
const domainToAgent = {
  'Sales Order': 'sales',
  'Business Partner': 'sales',
  'Sales Quotation': 'sales',

  'Vendor': 'purchase',
  'Purchase Orders': 'purchase',
  'Purchase Requisition': 'purchase',
  'Goods Movement': 'purchase',
  'Vendor Invoice': 'purchase',
  'Purchase Optimizer': 'purchase',

  'Human Resource': 'hr',
  'Policy Repository': 'hr',

  'Cost Center': 'finance',
  'Analytics': 'finance',
  'Finance Analyst': 'finance',
  'Vendor Ledger': 'finance',

  'Inventory': 'inventory',
};

// STEP 1: Dashboard URLs
const dashboardLinks = {
  'Sales Order': 'https://cd9d-205-254-163-120.ngrok-free.app/sales-order/',
  'Purchase Orders': 'https://cd9d-205-254-163-120.ngrok-free.app/purchase-order/',
  'Purchase Requisition': 'https://cd9d-205-254-163-120.ngrok-free.app/purchase-requisition/',
  'Goods Movement': 'https://cd9d-205-254-163-120.ngrok-free.app/goods-receipt/',
  'Vendor Invoice': 'https://cd9d-205-254-163-120.ngrok-free.app/payments/',
  'Default': 'https://your-default-dashboard-url.com'
};

// STEP 2: Domains that have dashboards
const dashboardVisibleDomains = [
  'Sales Order',
  'Purchase Orders',
  'Purchase Requisition',
  'Goods Movement',
  'Vendor Invoice',
  'Analytics',
  'Finance Analyst',
  'Palmtop'
];

// Domains whose dashboard is rendered in-app as a full-screen overlay rather
// than opened as an external URL. ChatInterface picks which overlay to show.
const inAppDashboardDomains = [
  'Purchase Orders',
  'Purchase Requisition',
  'Goods Movement',
  'Vendor Invoice',
  'Analytics',
  'Finance Analyst',
  'Palmtop'
];

const NavBar = ({
  onNewChat,
  currentConversationId,
  domain,
  onDomainChange,
  onToggleHistory,
  onToggleTheme,
  themeMode,
  onOpenDashboard
}) => {
  const theme = useTheme();

  const [canViewDashboard, setCanViewDashboard] = React.useState(false);

  // STEP 3: Load analytics permission
  React.useEffect(() => {
    const load = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email;

      if (!email || !domain) {
        setCanViewDashboard(false);
        return;
      }

      // Palmtop's dashboard follows its chat access: anyone who can see the
      // agent gets it, no Supabase 'analytics' grant needed.
      if (domain === 'Palmtop') {
        setCanViewDashboard(true);
        return;
      }

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('agents')
        .eq('email', email)
        .single();

      const agents = profile?.agents || {};
      const agentKey = domainToAgent[domain];

      if (!agentKey) {
        setCanViewDashboard(false);
        return;
      }

      const hasAnalytics = agents?.[agentKey]?.includes("analytics");

      setCanViewDashboard(Boolean(hasAnalytics));
    };

    load();
  }, [domain]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  const handleGoToDashboard = () => {
    // Purchase-agent domains open the in-app dashboard overlay
    if (inAppDashboardDomains.includes(domain) && onOpenDashboard) {
      onOpenDashboard();
      return;
    }
    const url = dashboardLinks[domain] || dashboardLinks['Default'];
    window.open(url, "_blank");
  };

  const getShortConversationId = (id) => {
    if (!id) return '';
    const parts = id.split('_');
    return parts.length > 1 ? parts[1] : id;
  };

  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState(null);
  const isSettingsMenuOpen = Boolean(settingsAnchorEl);

  const handleSettingsClick = (event) => setSettingsAnchorEl(event.currentTarget);
  const handleSettingsClose = () => setSettingsAnchorEl(null);

  return (
    <AppBar position="static" sx={{ bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: { xs: '48px', sm: '56px' },
          paddingX: { xs: 1, sm: 2 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DomainSelector onDomainChange={onDomainChange} value={domain} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>

          {/* Theme toggle */}
          <IconButton
            aria-label="Toggle theme"
            onClick={onToggleTheme}
            size="small"
            sx={{
              color: theme.palette.text.primary,
              border: `2px solid ${theme.palette.divider}`,
              borderRadius: "999px",
              p: "4px",
              "&:hover": { bgcolor: theme.palette.action.hover, borderColor: theme.palette.text.primary },
            }}
          >
            {themeMode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>

          {/* FINAL: dashboard only if domain supports it AND supabase says analytics is allowed */}
          {dashboardVisibleDomains.includes(domain) && canViewDashboard && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleGoToDashboard}
              startIcon={<LaunchIcon fontSize="small" />}
              sx={(theme) => ({
                fontWeight: 600,
                borderRadius: '999px',
                px: 1.5,
                py: 0.25,
                minHeight: 32,
                fontSize: 14,
                lineHeight: 1.2,
                color: theme.palette.text.primary,
                backgroundColor: 'transparent',
                border: `2px solid ${theme.palette.divider}`,
                '& .MuiButton-startIcon': { mr: 0.75 },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.text.primary,
                },
              })}
            >
              Go to Dashboard
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={onNewChat}
            size="small"
            sx={{
              fontWeight: 600,
              borderRadius: "999px",
              px: 1.5,
              py: 0.25,
              fontSize: 14,
              lineHeight: 1.2,
              minHeight: 32,
              color: theme.palette.text.primary,
              border: `2px solid ${theme.palette.divider}`,
              bgcolor: "transparent",
              "&:hover": { bgcolor: theme.palette.action.hover, borderColor: theme.palette.text.primary },
            }}
          >
            New Chat
          </Button>

          <IconButton
            onClick={handleSettingsClick}
            size="medium"
            sx={(theme) => ({
              color: theme.palette.text.primary,
              border: `2px solid ${theme.palette.divider}`,
              borderRadius: '999px',
              padding: '6px',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.text.primary
              }
            })}
          >
            <SettingsIcon />
          </IconButton>

          <Menu
            anchorEl={settingsAnchorEl}
            open={isSettingsMenuOpen}
            onClose={handleSettingsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: (theme) => ({
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '6px',
                minWidth: '160px',
                mt: 1
              })
            }}
          >
            <MenuItem onClick={() => { handleSettingsClose(); onToggleHistory(); }}>
              <HistoryIcon sx={{ fontSize: '1rem', mr: 1 }} />
              History
            </MenuItem>
            <Divider sx={(theme) => ({ borderColor: theme.palette.divider, my: 0.5 })} />
            <MenuItem onClick={() => { handleSettingsClose(); handleLogout(); }}>
              <LogoutIcon sx={{ fontSize: '1rem', mr: 1 }} />
              Log Out
            </MenuItem>
            <Divider sx={(theme) => ({ borderColor: theme.palette.divider, my: 0.5 })} />
            <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem', pointerEvents: 'none' }}>
              Chat ID: {getShortConversationId(currentConversationId)}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <Divider sx={(theme) => ({ borderColor: theme.palette.divider, my: 0.5 })} />
    </AppBar>
  );
};

export default NavBar;
